import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Filter, Grid, List, SlidersHorizontal } from 'lucide-react';
import { ProductGrid } from '../components/ProductGrid';
import { ProductFilters } from '../components/ProductFilters';
import { getProducts, filterProducts, sortProducts, getCategories, getColors, getSizes, getPriceRange } from '../data/products';
import { Product, ProductFilters as FilterType } from '../types';

export const Shop = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState<Product[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [sortBy, setSortBy] = useState('popular');
  const [filters, setFilters] = useState<FilterType>({});
  const [isLoading, setIsLoading] = useState(true);
  const [filterData, setFilterData] = useState({
    categories: [] as string[],
    colors: [] as string[],
    sizes: [] as string[],
    priceRange: [0, 0] as [number, number],
  });

  // Load products and filter data
  useEffect(() => {
    const loadProductsData = async () => {
      try {
        const [productsData, categories, colors, sizes, priceRange] = await Promise.all([
          getProducts(),
          getCategories(),
          getColors(),
          getSizes(),
          getPriceRange(),
        ]);
        setProducts(productsData);
        setFilterData({ categories, colors, sizes, priceRange });
        setIsLoading(false);
      } catch (error) {
        console.error('Error loading products:', error);
        setIsLoading(false);
      }
    };
    loadProductsData();
  }, []);

  // Apply filters and sorting
  useEffect(() => {
    if (products.length > 0) {
      let filtered = filterProducts(products, filters);
      filtered = sortProducts(filtered, sortBy);
      setFilteredProducts(filtered);
    }
  }, [products, filters, sortBy]);

  // Initialize filters from URL params
  useEffect(() => {
    const newFilters: FilterType = {};
    
    const category = searchParams.get('category');
    if (category) newFilters.category = category;
    
    const search = searchParams.get('search');
    if (search) newFilters.search = search;
    
    const newParam = searchParams.get('new');
    if (newParam === 'true') newFilters.isNew = true;
    
    const saleParam = searchParams.get('sale');
    if (saleParam === 'true') newFilters.isSale = true;
    
    const featuredParam = searchParams.get('featured');
    if (featuredParam === 'true') {
      newFilters.isNew = true;
      newFilters.isSale = true;
    }
    
    setFilters(newFilters);
  }, [searchParams]);

  const handleFilterChange = (newFilters: FilterType) => {
    setFilters(newFilters);
    
    // Update URL params
    const params = new URLSearchParams();
    if (newFilters.category) params.set('category', newFilters.category);
    if (newFilters.search) params.set('search', newFilters.search);
    if (newFilters.isNew) params.set('new', 'true');
    if (newFilters.isSale) params.set('sale', 'true');
    
    setSearchParams(params);
  };

  const clearFilters = () => {
    setFilters({});
    setSearchParams({});
  };

  const sortOptions = [
    { value: 'popular', label: 'Most Popular' },
    { value: 'newest', label: 'Newest First' },
    { value: 'price-low-high', label: 'Price: Low to High' },
    { value: 'price-high-low', label: 'Price: High to Low' },
    { value: 'name-a-z', label: 'Name: A to Z' },
    { value: 'name-z-a', label: 'Name: Z to A' },
  ];

  return (
    <div className="min-h-screen bg-white">
      <div className="container py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-charcoal-900 mb-4">
            {filters.search ? `Search Results for "${filters.search}"` : 'Shop All Products'}
          </h1>
          <p className="text-charcoal-600">
            {filteredProducts.length} products found
          </p>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-charcoal-600">Loading products...</p>
            </div>
          </div>
        ) : (
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Filters Sidebar */}
            <div className={`lg:w-64 ${showFilters ? 'block' : 'hidden lg:block'}`}>
            <div className="bg-white rounded-lg shadow-sm p-6 sticky top-24">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-gray-900">Filters</h2>
                <button
                  onClick={clearFilters}
                  className="text-sm text-primary-600 hover:text-primary-700"
                >
                  Clear All
                </button>
              </div>
              
              <ProductFilters
                filters={filters}
                onFiltersChange={handleFilterChange}
                categories={filterData.categories}
                colors={filterData.colors}
                sizes={filterData.sizes}
                priceRange={filterData.priceRange}
              />
            </div>
          </div>

          {/* Products Section */}
          <div className="flex-1">
            {/* Toolbar */}
            <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                {/* Mobile Filter Toggle */}
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="lg:hidden btn-outline btn-sm flex items-center space-x-2"
                >
                  <Filter className="h-4 w-4" />
                  <span>Filters</span>
                </button>

                {/* Sort Options */}
                <div className="flex items-center space-x-4">
                  <label className="text-sm font-medium text-gray-700">Sort by:</label>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="border border-gray-300 rounded-md px-3 py-1 text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    {sortOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* View Mode Toggle */}
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`p-2 rounded-md transition-colors ${
                      viewMode === 'grid' ? 'bg-primary-100 text-primary-600' : 'text-gray-400 hover:text-gray-600'
                    }`}
                  >
                    <Grid className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`p-2 rounded-md transition-colors ${
                      viewMode === 'list' ? 'bg-primary-100 text-primary-600' : 'text-gray-400 hover:text-gray-600'
                    }`}
                  >
                    <List className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Products Grid */}
            {filteredProducts.length > 0 ? (
              <ProductGrid 
                products={filteredProducts} 
                columns={viewMode === 'grid' ? 3 : 1}
              />
            ) : (
              <div className="text-center py-12">
                <SlidersHorizontal className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No products found</h3>
                <p className="text-gray-500 mb-6">
                  Try adjusting your filters or search terms
                </p>
                <button
                  onClick={clearFilters}
                  className="btn-primary btn-md"
                >
                  Clear Filters
                </button>
              </div>
            )}
          </div>
        </div>
        )}
      </div>
    </div>
  );
};
